import { useCallback, useEffect, useRef, useState } from 'react';

import { type CalendarShiftListResponse } from '@libraries/typescript/api_client';

export function isUpcomingOpenShift(
    shift: Pick<
        CalendarShiftListResponse['shifts'][number],
        'status' | 'assigned_caregiver_id' | 'shift_start_time'
    >,
    now: number,
) {
    return (
        shift.status === 'open' &&
        !shift.assigned_caregiver_id &&
        new Date(shift.shift_start_time).getTime() > now
    );
}

/** Keeps the map cohort and client chooser on the same rolling 30-day policy. */
export function useCoverageOpenShiftWindow(enabled = true) {
    const [rangeStart, setRangeStart] = useState(() =>
        new Date().toISOString(),
    );
    const [wasEnabled, setWasEnabled] = useState(enabled);
    const lastRefresh = useRef(new Date(rangeStart).getTime());
    const refresh = useCallback(() => {
        lastRefresh.current = Date.now();
        setRangeStart(new Date(lastRefresh.current).toISOString());
    }, []);
    useEffect(() => {
        if (!enabled) {
            setWasEnabled(false);
            return;
        }
        if (!wasEnabled) {
            refresh();
            setWasEnabled(true);
        }
        const refreshIfVisible = () => {
            if (
                document.visibilityState === 'visible' &&
                Date.now() - lastRefresh.current >= 15_000
            ) {
                refresh();
            }
        };
        refreshIfVisible();
        window.addEventListener('focus', refreshIfVisible);
        document.addEventListener('visibilitychange', refreshIfVisible);
        return () => {
            window.removeEventListener('focus', refreshIfVisible);
            document.removeEventListener('visibilitychange', refreshIfVisible);
        };
    }, [enabled, wasEnabled, refresh]);
    return {
        isReady: enabled === wasEnabled,
        rangeStart,
        rangeEnd: new Date(
            new Date(rangeStart).getTime() + 30 * 86400000,
        ).toISOString(),
        refresh,
    };
}
